import { useLayoutEffect } from 'react';

/**
 * Locks background (body) scrolling while a modal/overlay is mounted, and
 * restores the previous scroll position when it unmounts.
 *
 * Two layers of defense:
 *  1. `position: fixed` + top-offset on <body>, which neutralizes document
 *     scrolling on most browsers (including the visual jump on iOS).
 *  2. A non-passive `touchmove` guard for iOS Safari/WebKit, where the
 *     position:fixed trick alone can still let a touch-drag scroll the page
 *     behind the overlay. The guard cancels touch scrolling unless the gesture
 *     started inside a genuinely scrollable element (e.g. the modal's own
 *     `overflow-y-auto` panel), so the panel still scrolls normally.
 */
export function useBodyScrollLock() {
  useLayoutEffect(() => {
    const scrollY = window.scrollY;
    const body = document.body;
    const prev = {
      position: body.style.position,
      top: body.style.top,
      left: body.style.left,
      right: body.style.right,
      width: body.style.width,
      overflow: body.style.overflow,
    };

    body.style.position = 'fixed';
    body.style.top = `-${scrollY}px`;
    body.style.left = '0';
    body.style.right = '0';
    body.style.width = '100%';
    body.style.overflow = 'hidden';

    // Decide on touchstart whether the gesture originates inside a scrollable
    // element; only then do we let iOS handle the scroll natively.
    let allowTouchScroll = false;
    const onTouchStart = (e) => {
      allowTouchScroll = false;
      let el = e.target;
      while (el && el !== body) {
        const style = window.getComputedStyle(el);
        const overflowY = style.overflowY;
        if ((overflowY === 'auto' || overflowY === 'scroll') && el.scrollHeight > el.clientHeight) {
          allowTouchScroll = true;
          break;
        }
        el = el.parentElement;
      }
    };
    const onTouchMove = (e) => {
      // Allow pinch-zoom and scrolling inside scrollable panels; block the rest.
      if (allowTouchScroll || (e.touches && e.touches.length > 1)) return;
      if (e.cancelable) e.preventDefault();
    };

    document.addEventListener('touchstart', onTouchStart, { passive: true });
    document.addEventListener('touchmove', onTouchMove, { passive: false });

    return () => {
      document.removeEventListener('touchstart', onTouchStart);
      document.removeEventListener('touchmove', onTouchMove);
      body.style.position = prev.position;
      body.style.top = prev.top;
      body.style.left = prev.left;
      body.style.right = prev.right;
      body.style.width = prev.width;
      body.style.overflow = prev.overflow;
      window.scrollTo(0, scrollY);
    };
  }, []);
}
